import { fetchSalesStatistics } from '../../api';
import { calculateOrdersChartOptions } from './orders-chart-options';
import { chartConfig } from '@/features/order-statistics/config';
import { Env } from '@/shared/config/environment';

const state = {
  salesStatistics: null,
  isLoadingSales: false,
  salesError: null,
  activeFilters: {
    product: null,
    partner: null,
    period: [],
    periodMode: null,
  },

  ordersAmountChartOptions: null,
  ordersEarningsChartOptions: null,
  ordersCountChartOptions: null,
};

const mutations = {
  SET_SALES_STATISTICS(state, stats) {
    state.salesStatistics = stats;
  },
  SET_LOADING_SALES(state, isLoading) {
    state.isLoadingSales = isLoading;
  },
  SET_SALES_ERROR(state, error) {
    state.salesError = error;
  },
  SET_ORDERS_AMOUNT_CHART_OPTIONS(state, options) {
    state.ordersAmountChartOptions = options;
  },
  SET_ORDERS_EARNINGS_CHART_OPTIONS(state, options) {
    state.ordersEarningsChartOptions = options;
  },
  SET_ORDERS_COUNT_CHART_OPTIONS(state, options) {
    state.ordersCountChartOptions = options;
  },
  SET_ACTIVE_FILTERS(state, filter) {
    state.activeFilters = filter;
  },
  SET_ACTIVE_FILTERS_PERIOD_MODE(state, mode) {
    state.activeFilters.periodMode = mode;
  },
};

const actions = {
  clearSalesData({ commit }) {
    commit('SET_SALES_STATISTICS', null);
    commit('SET_ORDERS_AMOUNT_CHART_OPTIONS', null);
    commit('SET_ORDERS_COUNT_CHART_OPTIONS', null);
    commit('SET_ORDERS_EARNINGS_CHART_OPTIONS', null);
  },
  async fetchSalesStatistics({ commit, dispatch, state }) {
    commit('SET_LOADING_SALES', true);
    commit('SET_SALES_ERROR', null);
    dispatch('clearSalesData');

    try {
      const stats = await fetchSalesStatistics(state.activeFilters);
      commit('SET_SALES_STATISTICS', stats);
      dispatch('calculateOrdersChartOptions');

      return stats;
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося отримати статистику продажів';

      commit('SET_SALES_ERROR', errorMessage);
      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося отримати статистику продажів',
          message: errorMessage,
        }, { root: true });
      }

      throw error;
    } finally {
      commit('SET_LOADING_SALES', false);
    }
  },
  calculateOrdersChartOptions: ({ state, commit }) => {
    if (state.salesStatistics) {
      const chartAmountOptions = calculateOrdersChartOptions(state.salesStatistics, 'total_amount', 'currency');
      commit('SET_ORDERS_AMOUNT_CHART_OPTIONS', chartAmountOptions);

      const chartEarningsOptions = calculateOrdersChartOptions(state.salesStatistics, 'total_earnings', 'currency');
      commit('SET_ORDERS_EARNINGS_CHART_OPTIONS', chartEarningsOptions);

      const chartCountOptions = calculateOrdersChartOptions(state.salesStatistics, 'order_count', 'count');
      commit('SET_ORDERS_COUNT_CHART_OPTIONS', chartCountOptions);
    }
  },
  setupFilter: ({ state, commit, dispatch }, { filters, mode = 'replace', refetchData = true }) => {
    if (mode === 'replace') {
      commit('SET_ACTIVE_FILTERS', filters);
    } else {
      commit('SET_ACTIVE_FILTERS', { ...state.activeFilters, ...filters });
    }

    if (refetchData) {
      dispatch('fetchSalesStatistics');
    }
  },
  setupPeriodMode: ({ commit }, mode) => {
    commit('SET_ACTIVE_FILTERS_PERIOD_MODE', mode);
  },
};

const getters = {
  salesStatistics: (state) => state.salesStatistics,
  isLoadingSales: (state) => state.isLoadingSales,
  salesError: (state) => state.salesError,

  isOrdersDataReady: (state) => { return state.isLoadingSales === false && state.salesStatistics !== null &&
    state.ordersAmountChartOptions !== null && state.ordersEarningsChartOptions !== null
    && state.ordersCountChartOptions !== null; },
  isChartEmpty: (state) => !state.salesStatistics ||
    Object.values(state.salesStatistics).reduce((total, month) => total + (month?.order_count || 0), 0) === 0,

  getSalesStatistics: (state) => state.salesStatistics,
  getStatisticChartOptions: (state) => (type) => { return state[`orders${type.charAt(0).toUpperCase() + type.slice(1)}ChartOptions`]; },
  getEmptyOrdersChartMessage: () => chartConfig.emptyMessages['message-1'],
  getActiveFilters: (state) => state.activeFilters,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
